import { buildCatalog, type PricingData } from '@rpl/pricing';
import { useMemo } from 'react';
import type { LocalPhoto, UiPrint } from '../../lib/types';
import { useCartExtras } from '../../store/cartExtras';
import { useStore } from '../../store/store';

/**
 * Checkout blockers surfaced on the cart:
 *  - BlockedBanner: unresolved low-res prints (client pre-check or server 422
 *    lowres_unresolved) with a "Fix in Size & Paper" jump.
 *  - NoticeBanner: catalog_not_synced (503) and generic checkout failures.
 */
export function BlockedBanner({
  prints,
  photos,
  pricing,
}: {
  prints: UiPrint[];
  photos: LocalPhoto[];
  pricing: PricingData;
}) {
  const blockedIds = useCartExtras((s) => s.blockedPrintIds);
  const dismiss = useCartExtras((s) => s.dismissBlocked);
  const setStep = useStore((s) => s.setStep);

  const rows = useMemo(() => {
    if (!blockedIds || blockedIds.length === 0) return [];
    const catalog = buildCatalog(pricing);
    const byId = new Map(prints.map((p) => [p.id, p]));
    const photoById = new Map(photos.map((p) => [p.id, p]));
    return blockedIds
      .map((id) => byId.get(id))
      .filter((p): p is UiPrint => !!p)
      .map((p) => ({
        id: p.id,
        filename: photoById.get(p.photoId)?.filename ?? 'photo',
        sizeLabel: catalog.get(p.sizeId)?.label ?? p.sizeId,
      }));
  }, [blockedIds, prints, photos, pricing]);

  if (rows.length === 0) return null;

  return (
    <div className="cblocked" role="alert">
      <div className="cblocked__head">
        <span className="cblocked__badge">✕</span>
        <div className="cblocked__title">
          {rows.length === 1
            ? '1 print is too low to order'
            : `${rows.length} prints are too low to order`}
        </div>
        <button type="button" className="cblocked__x" onClick={dismiss} aria-label="Dismiss">
          ✕
        </button>
      </div>
      <ul className="cblocked__list">
        {rows.map((r) => (
          <li key={r.id}>
            <strong>{r.filename}</strong> · {r.sizeLabel}
          </li>
        ))}
      </ul>
      <div className="cblocked__foot">
        <span>Swap each one to a smaller size, remove it, or choose &ldquo;Print anyway&rdquo;.</span>
        <button
          type="button"
          className="btn btn--primary cblocked__fix"
          onClick={() => {
            dismiss();
            setStep(1);
          }}
        >
          Fix in Size &amp; Paper
        </button>
      </div>
    </div>
  );
}

export function NoticeBanner() {
  const notice = useCartExtras((s) => s.notice);
  const dismiss = useCartExtras((s) => s.dismissNotice);
  if (!notice || notice.kind === 'handoff') return null; // handoff errors render on the interstitial
  return (
    <div className={`cnotice${notice.kind === 'catalog' ? ' cnotice--info' : ''}`} role="alert">
      <span className="cnotice__msg">{notice.message}</span>
      <button type="button" className="cnotice__x" onClick={dismiss} aria-label="Dismiss">
        ✕
      </button>
    </div>
  );
}
